import * as React from 'react'
import { Link2, Loader2, Search } from 'lucide-react'
import type { ClientDetailDto } from '@lib/dto/client.dto'
import type { HcpCustomerDto } from '@lib/dto/housecall-pro.dto'
import {
  hcpCustomerDraft,
  type HcpAddressDraft,
  type HcpCustomerDraft,
} from '@lib/helpers/hcp-customers'
import {
  HCP_SEARCH_MIN_LENGTH,
  useCreateHcpCustomer,
  useHcpCustomerSearch,
  useLinkHcpCustomer,
} from '@/hooks/useClientHcp'
import { useDebounced } from '@/hooks/useDebounced'
import { errorMessage } from '@/helpers/logger'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

/** "4502 98th Street, Lubbock, TX 79424", as the confirm lists what is sent. */
function draftAddressLine(address: HcpAddressDraft): string {
  const street = [address.street, address.streetLine2]
    .filter((part): part is string => part !== null)
    .join(', ')
  return `${street}, ${address.city}, ${address.state} ${address.zip}`
}

/**
 * Where the search starts: the first word of the client's name, "The" aside.
 *
 * HCP returns only customers matching every word searched for, and the office
 * rarely filed an association under its full name: "Kingsgate Owners
 * Association" finds nothing where "Kingsgate" finds it.
 */
function searchSeed(clientName: string): string {
  const words = clientName.trim().split(/\s+/)
  const first = words[0]?.toLowerCase() === 'the' ? words[1] : words[0]
  return first ?? ''
}

interface HcpCustomerLinkProps {
  client: ClientDetailDto
  /** Whether this deployment may create customers in HCP. */
  writesEnabled: boolean
}

/**
 * Which Housecall Pro customer the client is: integration metadata, so a
 * quiet chip under the heading rather than a field in the form. It opens the
 * dialog that links, changes, unlinks or creates it.
 *
 * Shown linked or not, so a client nobody has linked yet is visible as one
 * before a job is needed for it.
 */
export function HcpCustomerLink({ client, writesEnabled }: HcpCustomerLinkProps) {
  const [open, setOpen] = React.useState<boolean>(false)
  const linked = client.hcpCustomer

  return (
    <>
      <button
        type="button"
        className={cn(
          'inline-flex max-w-full items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs transition-colors',
          linked === null
            ? 'border-default bg-surface text-muted-text hover:border-strong hover:bg-subtle'
            : 'border-transparent bg-pill-blue text-pill-blue-text hover:border-strong',
        )}
        onClick={() => {
          setOpen(true)
        }}
      >
        <Link2 className="h-3.5 w-3.5 shrink-0" />
        <span className="shrink-0 font-medium">Housecall Pro</span>
        <span className="truncate">
          {linked === null ? (
            <span className="italic">Not linked</span>
          ) : (
            linked.name
          )}
        </span>
      </button>
      {open ? (
        <HcpCustomerDialog
          client={client}
          writesEnabled={writesEnabled}
          onClose={() => {
            setOpen(false)
          }}
        />
      ) : null}
    </>
  )
}

type DialogStep = 'search' | 'confirm-create'

interface HcpCustomerDialogProps {
  client: ClientDetailDto
  writesEnabled: boolean
  onClose: () => void
}

/**
 * Link the client to a customer found by searching HCP, or create one.
 *
 * The search starts from the client's name, because that is usually how the
 * office filed them (see `searchSeed`). Creating is a second step that lists
 * exactly what HCP will receive, from the same draft the API sends: it lands
 * in the live account the office works from.
 */
function HcpCustomerDialog({ client, writesEnabled, onClose }: HcpCustomerDialogProps) {
  const linkCustomer = useLinkHcpCustomer()
  const createCustomer = useCreateHcpCustomer()

  const [query, setQuery] = React.useState<string>(() => searchSeed(client.name))
  const [step, setStep] = React.useState<DialogStep>('search')
  const [error, setError] = React.useState<string | null>(null)

  const debouncedQuery = useDebounced(query, 300)
  const search = useHcpCustomerSearch(debouncedQuery)
  const draft = hcpCustomerDraft(client, client.contacts, client.sites)
  const busy = linkCustomer.isPending || createCustomer.isPending

  async function handleLink(customerId: string | null): Promise<void> {
    setError(null)
    try {
      await linkCustomer.mutateAsync({ clientId: client.id, customerId })
      onClose()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  async function handleCreate(): Promise<void> {
    setError(null)
    try {
      await createCustomer.mutateAsync(client.id)
      onClose()
    } catch (caught) {
      setError(errorMessage(caught, 'Something went wrong. Please try again.'))
    }
  }

  function renderLinked() {
    if (client.hcpCustomer === null) return null
    // An address belongs to one customer, so the API clears the sites' links
    // with any change of customer. Say so before it happens.
    const linkedSites = client.sites.filter((site) => site.hcpAddress !== null).length
    return (
      <div className="flex items-center justify-between gap-3 rounded-md border border-default bg-subtle px-3 py-2">
        <div className="min-w-0">
          <p className="text-xs text-muted-text">Linked to</p>
          <p className="truncate text-sm font-medium">{client.hcpCustomer.name}</p>
          {linkedSites > 0 ? (
            <p className="text-xs whitespace-normal text-muted-text">
              Unlinking, or linking another customer, also unlinks{' '}
              {linkedSites === 1 ? 'the site' : `${String(linkedSites)} sites`}{' '}
              from this customer’s addresses.
            </p>
          ) : null}
        </div>
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => {
            void handleLink(null)
          }}
        >
          Unlink
        </Button>
      </div>
    )
  }

  function renderResults() {
    if (debouncedQuery.trim().length < HCP_SEARCH_MIN_LENGTH) {
      return (
        <p className="px-3 py-6 text-center text-sm text-muted-text">
          Type at least two letters of a name, email or phone number.
        </p>
      )
    }
    if (search.isPending) {
      return (
        <div className="flex justify-center py-6">
          <Loader2 className="h-4 w-4 animate-spin text-muted-text" />
        </div>
      )
    }
    if (search.isError) {
      return (
        <p className="px-3 py-6 text-center text-sm text-destructive">
          {errorMessage(search.error, 'Something went wrong. Please try again.')}
        </p>
      )
    }
    if (search.data.customers.length === 0) {
      return (
        <div className="px-3 py-6 text-center text-sm">
          <p className="text-muted-text">
            No Housecall Pro customer matches “{debouncedQuery.trim()}”.
          </p>
          <p className="mt-1 text-xs text-muted-text">
            It only finds customers matching every word, so try one: a name, a
            street, an email or a phone number.
          </p>
        </div>
      )
    }
    return (
      <>
        <ul className="divide-y divide-default">
          {search.data.customers.map((customer) => (
            <CustomerRow
              key={customer.id}
              customer={customer}
              clientId={client.id}
              disabled={busy}
              onLink={() => {
                void handleLink(customer.id)
              }}
            />
          ))}
        </ul>
        {search.data.total > search.data.customers.length ? (
          <p className="border-t border-default px-3 py-2 text-xs text-muted-text">
            The first {search.data.customers.length} of {search.data.total}.
            Add more of the name to narrow it.
          </p>
        ) : null}
      </>
    )
  }

  function renderCreateOffer() {
    if (client.hcpCustomer !== null) return null
    if (!writesEnabled) {
      return (
        <p className="text-xs text-muted-text">
          Not there? Creating customers is switched off in Settings. Create it
          in Housecall Pro, then search for it here.
        </p>
      )
    }
    if (draft === null) {
      return (
        <p className="text-xs text-muted-text">
          Not there? Mark a contact primary to create the customer from: Housecall
          Pro needs a person’s name, email or phone number.
        </p>
      )
    }
    return (
      <p className="text-xs text-muted-text">
        Not there?{' '}
        <button
          type="button"
          className="font-medium text-brand underline-offset-4 hover:underline"
          onClick={() => {
            setError(null)
            setStep('confirm-create')
          }}
        >
          Create it in Housecall Pro
        </button>
      </p>
    )
  }

  if (step === 'confirm-create' && draft !== null) {
    return (
      <Dialog
        open
        onOpenChange={(next) => {
          if (!next && !busy) onClose()
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create {client.name} in Housecall Pro?</DialogTitle>
            <DialogDescription>
              This adds a customer to the live Housecall Pro account, where the
              office will see it. It cannot be undone from here.
            </DialogDescription>
          </DialogHeader>
          <CustomerDraftSummary draft={draft} />
          {error ? <p className="text-sm text-destructive">{error}</p> : null}
          <DialogFooter>
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => {
                setError(null)
                setStep('search')
              }}
            >
              Back
            </Button>
            <Button
              size="sm"
              disabled={busy}
              onClick={() => {
                void handleCreate()
              }}
            >
              {createCustomer.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : null}
              Create customer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next && !busy) onClose()
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Housecall Pro customer</DialogTitle>
          <DialogDescription>
            {client.name}’s jobs are created on this customer in Housecall Pro.
          </DialogDescription>
        </DialogHeader>

        {renderLinked()}

        <div className="space-y-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-text" />
            <Input
              aria-label="Search Housecall Pro customers"
              className="pl-8"
              value={query}
              placeholder="Name, email or phone"
              onChange={(event) => {
                setQuery(event.target.value)
              }}
            />
          </div>
          <div className="max-h-72 overflow-y-auto rounded-md border border-default">
            {renderResults()}
          </div>
        </div>

        {error ? <p className="text-sm text-destructive">{error}</p> : null}
        {renderCreateOffer()}
      </DialogContent>
    </Dialog>
  )
}

interface CustomerRowProps {
  customer: HcpCustomerDto
  clientId: string
  disabled: boolean
  onLink: () => void
}

/**
 * One customer HCP found: enough to tell two "Smith"s apart (how to reach
 * them, where they are), and why it cannot be chosen when another client
 * already has it.
 */
function CustomerRow({ customer, clientId, disabled, onLink }: CustomerRowProps) {
  const reach = [customer.email, customer.phone]
    .filter((part): part is string => part !== null)
    .join(' · ')
  const [first, ...more] = customer.addresses

  function renderAction() {
    if (customer.linkedClient === null) {
      return (
        <Button size="sm" variant="outline" disabled={disabled} onClick={onLink}>
          Link
        </Button>
      )
    }
    if (customer.linkedClient.id === clientId) {
      return <span className="text-xs text-muted-text">Linked</span>
    }
    return (
      <span className="text-xs text-muted-text">
        Linked to {customer.linkedClient.name}
      </span>
    )
  }

  return (
    <li className="flex items-center justify-between gap-3 px-3 py-2 hover:bg-subtle">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium">{customer.name}</p>
        {reach === '' ? null : (
          <p className="truncate text-xs text-secondary-text">{reach}</p>
        )}
        <p className="truncate text-xs text-muted-text">
          {first === undefined ? 'No address' : first.label}
          {more.length > 0 ? ` and ${String(more.length)} more` : ''}
        </p>
      </div>
      <div className="shrink-0">{renderAction()}</div>
    </li>
  )
}

/** What HCP will receive, field by field, and which sites are left out and why. */
function CustomerDraftSummary({ draft }: { draft: HcpCustomerDraft }) {
  const person = [draft.firstName, draft.lastName]
    .filter((part): part is string => part !== null)
    .join(' ')
  const rows: { label: string; value: string }[] = [
    { label: 'Company', value: draft.company ?? '—' },
    { label: 'Name', value: person === '' ? '—' : person },
    { label: 'Email', value: draft.email ?? '—' },
    { label: 'Mobile', value: draft.mobileNumber ?? '—' },
  ]

  return (
    <div className="space-y-3 text-sm">
      <dl className="grid grid-cols-[6rem_1fr] gap-x-3 gap-y-1">
        {rows.map((row) => (
          <React.Fragment key={row.label}>
            <dt className="text-muted-text">{row.label}</dt>
            <dd className="min-w-0 truncate">{row.value}</dd>
          </React.Fragment>
        ))}
      </dl>
      <div>
        <p className="text-xs font-medium text-secondary-text">
          {draft.addresses.length === 0
            ? 'No addresses'
            : 'Addresses, each linked to its site'}
        </p>
        <ul className="mt-1 space-y-1">
          {draft.addresses.map((entry) => (
            <li key={entry.siteId} className="text-sm">
              <span className="font-medium">{entry.siteName}</span>
              <span className="text-secondary-text">
                {' · '}
                {draftAddressLine(entry.address)}
              </span>
            </li>
          ))}
        </ul>
        {draft.sitesWithoutAddress.length > 0 ? (
          <p className="mt-1 text-xs text-muted-text">
            Left out until their address has a street, city, state and ZIP:{' '}
            {draft.sitesWithoutAddress.join(', ')}.
          </p>
        ) : null}
      </div>
    </div>
  )
}
